import React, { useMemo, useState } from 'react';
import * as S from '../../pages/boleto/boleto-payment.style';
import { useTranslation } from 'react-i18next';
import { Sell } from '../../../common/types/venda';
import { Checkout } from '../../../common/types/checkout';
import { formatCurrency } from '../../../common/utils/format-currency';
import Barcode from 'react-barcode';
import { navigateExternalNoReferrer } from '../../../common/utils/navigate-no-referer';

type Props = { venda: Sell; checkout: Checkout };

const onlyDigits = (s?: string) => (s || '').replace(/\D/g, '');

const formatDigitableLine = (raw?: string) => {
  const d = onlyDigits(raw);
  if (d.length !== 47) return raw || '';
  const p = [
    d.slice(0, 5),
    d.slice(5, 10),
    d.slice(10, 15),
    d.slice(15, 21),
    d.slice(21, 26),
    d.slice(26, 32),
    d.slice(32, 33),
    d.slice(33, 47),
  ];
  return `${p[0]}.${p[1]} ${p[2]}.${p[3]} ${p[4]}.${p[5]} ${p[6]} ${p[7]}`;
};

export const BoletoCard: React.FC<Props> = ({ venda, checkout }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const totalAmount = useMemo(
    () => formatCurrency(venda?.valorPago, checkout?.moeda),
    [venda?.valorPago, checkout?.moeda],
  );

  const linha47 = onlyDigits(venda?.linhaDigitavel);
  const barcode44 = onlyDigits((venda as any)?.codigoBarras);

  const linhaFormatada = useMemo(() => formatDigitableLine(linha47), [linha47]);

  const handleCopyCode = async () => {
    await navigator.clipboard.writeText(linha47);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isLinhaOk = /^\d{47}$/.test(linha47);
  const isBarcodeOk = /^\d{44}$/.test(barcode44);
  const print = () => {
    navigateExternalNoReferrer(venda?.linkExternoBoleto ?? '', {
      newTab: true,
    });
  };

  return (
    <>
      <S.TitleSection>
        <S.Title>{t('payWithBoleto', { amount: totalAmount })}</S.Title>
        <S.Subtitle>{t('finishPurchase')}</S.Subtitle>
      </S.TitleSection>

      <S.InstructionsSection>
        <S.SectionTitle>{t('paymentInstructions')}</S.SectionTitle>
        <S.InstructionsText>{t('bankSlipInstructions')}</S.InstructionsText>
        <S.InstructionsText>{t('bankSlipApproval')}</S.InstructionsText>
      </S.InstructionsSection>

      <S.OrderDetails>
        <S.DetailItem>
          <S.DetailLabel>{t('orderId')}</S.DetailLabel>
          <S.DetailValue>{venda?.id}</S.DetailValue>
        </S.DetailItem>
        <S.DetailItem>
          <S.DetailLabel>{t('product')}</S.DetailLabel>
          <S.DetailValue>{venda?.produto?.nome}</S.DetailValue>
        </S.DetailItem>
        <S.DetailItem>
          <S.DetailLabel>{t('total')}</S.DetailLabel>
          <S.DetailValue>{totalAmount}</S.DetailValue>
        </S.DetailItem>
        <S.DetailItem>
          <S.DetailLabel>{t('paymentMethod')}</S.DetailLabel>
          <S.DetailValue>{t('bankSlip')}</S.DetailValue>
        </S.DetailItem>
        <S.DetailItem>
          <S.DetailLabel>{t('status')}</S.DetailLabel>
          <S.Status>{t('waitingPayment')}</S.Status>
        </S.DetailItem>
      </S.OrderDetails>

      <S.BarcodeSection>
        <S.BarcodeNumber>{linhaFormatada}</S.BarcodeNumber>

        <S.BarcodeImage aria-label={t('bankSlipBarcode')}>
          {isBarcodeOk ? (
            <Barcode
              value={barcode44}
              format="ITF"
              displayValue={false}
              margin={12}
              height={80}
              width={1.6}
            />
          ) : (
            <div
              style={{
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#a00',
              }}
            >
              {t('invalidBarcode')}
            </div>
          )}
        </S.BarcodeImage>
      </S.BarcodeSection>

      <S.ActionsContainer>
        <S.PrimaryButton onClick={handleCopyCode} disabled={!isLinhaOk}>
          {copied ? t('codeCopied') : t('copyDigitableLine')}
        </S.PrimaryButton>
        <S.SecondaryButton onClick={print}>
          {t('printBankSlip')}
        </S.SecondaryButton>
      </S.ActionsContainer>

      <S.ConfirmationText>
        {`${t('confirmationLine1')} ${t('confirmationLine2')}`}
      </S.ConfirmationText>
    </>
  );
};
